import {
    ApiUserPreferencesReqSchema,
    ApiUserPreferencesResSchema,
} from "@bridge-applications/pb-types";
import { authenticatedApiCall } from "../helpers/api";
import i18n from "./i18n";
import {
    clearPendingLocale,
    detectInitialLocale,
    getPendingLocale,
    storeLocale,
    storePendingLocale,
} from "./localeStorage";
import { normalizeLocale, type SupportedLocale } from "./locales";

export class LocalePreferenceError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "LocalePreferenceError";
    }
}

export function getActiveLocale(): SupportedLocale {
    return (
        normalizeLocale(i18n.language) ??
        normalizeLocale(i18n.resolvedLanguage) ??
        detectInitialLocale()
    );
}

async function applyLocale(locale: SupportedLocale): Promise<void> {
    await i18n.changeLanguage(locale);
    storeLocale(locale);
}

async function readServerLocale(): Promise<string | null> {
    const { body } = await authenticatedApiCall(
        "/api/v1/user/preferences",
        ApiUserPreferencesResSchema,
    );
    if (body.status !== "OK") {
        throw new LocalePreferenceError(i18n.t("settings.errors.load"));
    }
    return body.locale;
}

async function writeServerLocale(
    locale: SupportedLocale,
): Promise<SupportedLocale> {
    const request = ApiUserPreferencesReqSchema.parse({ locale });
    const { body } = await authenticatedApiCall(
        "/api/v1/user/preferences",
        ApiUserPreferencesResSchema,
        {
            method: "PATCH",
            body: JSON.stringify(request),
        },
    );
    if (body.status !== "OK") {
        throw new LocalePreferenceError(i18n.t("settings.errors.save"));
    }

    const saved = normalizeLocale(body.locale);
    if (!saved) {
        throw new LocalePreferenceError(i18n.t("settings.errors.unsupported"));
    }
    return saved;
}

export async function reconcileLocalePreference(
    persistedLocale: string | null,
): Promise<SupportedLocale> {
    const pendingLocale = getPendingLocale();
    if (pendingLocale) {
        await applyLocale(pendingLocale);
        try {
            const saved = await writeServerLocale(pendingLocale);
            clearPendingLocale();
            await applyLocale(saved);
            return saved;
        } catch {
            return pendingLocale;
        }
    }

    const normalizedPersisted = normalizeLocale(persistedLocale);
    const locale = normalizedPersisted ?? getActiveLocale();
    await applyLocale(locale);

    if (persistedLocale !== locale) {
        try {
            const saved = await writeServerLocale(locale);
            clearPendingLocale();
            await applyLocale(saved);
            return saved;
        } catch {
            storePendingLocale(locale);
        }
    }

    return locale;
}

export async function restoreLocalePreference(): Promise<SupportedLocale> {
    try {
        return await reconcileLocalePreference(await readServerLocale());
    } catch {
        if (getPendingLocale()) {
            return reconcileLocalePreference(null);
        }
        return getActiveLocale();
    }
}

export async function selectLocalePreference(
    locale: SupportedLocale,
): Promise<SupportedLocale> {
    storePendingLocale(locale);
    await applyLocale(locale);

    const saved = await writeServerLocale(locale);
    clearPendingLocale();
    await applyLocale(saved);
    return saved;
}
